/**
 * @file Calendar 月历面板
 * @description DatePicker / DateRangePicker 共用的月份网格（非独立业务控件）。
 *
 * @whenToUse 嵌入日期选择浮层。
 * @whenNotToUse 业务页直接展示大日历（应包一层 DatePicker）。
 */

import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import {
  WEEKDAY_LABELS,
  formatDate,
  formatMonthTitle,
  getMonthCells,
  isInRange,
  isSameDay,
} from '@/lib/date.ts'

export type CalendarProps = {
  /** 当前展示的月份 */
  viewMonth: Date
  /** 切换月份 */
  onViewMonthChange: (month: Date) => void
  /** 点击某一天 */
  onSelectDay: (day: Date) => void
  /** 单选选中日 */
  selected?: Date | null
  /** 区间起 */
  rangeFrom?: Date | null
  /** 区间止 */
  rangeTo?: Date | null
  /** 区间选择时的悬停预览日 */
  hoverDay?: Date | null
  /** 悬停日变化 */
  onHoverDayChange?: (day: Date | null) => void
  className?: string
}

export function Calendar({
  viewMonth,
  onViewMonthChange,
  onSelectDay,
  selected = null,
  rangeFrom = null,
  rangeTo = null,
  hoverDay = null,
  onHoverDayChange,
  className,
}: CalendarProps) {
  const cells = getMonthCells(viewMonth)
  const today = new Date()

  let previewFrom = rangeFrom
  let previewTo = rangeTo
  if (rangeFrom && !rangeTo && hoverDay) {
    if (formatDate(hoverDay) < formatDate(rangeFrom)) {
      previewFrom = hoverDay
      previewTo = rangeFrom
    } else {
      previewTo = hoverDay
    }
  }

  return (
    <div className={cn('w-72 p-3', className)}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label="上一月"
          className="inline-flex size-8 items-center justify-center rounded-md text-muted transition-opacity duration-200 hover:opacity-90"
          onClick={() =>
            onViewMonthChange(
              new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1),
            )
          }
        >
          <ChevronLeft className="size-4" aria-hidden />
        </button>
        <div className="text-sm font-medium text-foreground">
          {formatMonthTitle(viewMonth)}
        </div>
        <button
          type="button"
          aria-label="下一月"
          className="inline-flex size-8 items-center justify-center rounded-md text-muted transition-opacity duration-200 hover:opacity-90"
          onClick={() =>
            onViewMonthChange(
              new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1),
            )
          }
        >
          <ChevronRight className="size-4" aria-hidden />
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-0.5">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="flex h-8 items-center justify-center text-xs text-muted"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((day, index) => {
          if (!day) {
            return <div key={`empty-${index}`} className="h-8" />
          }

          const key = formatDate(day)
          const isSelected = selected ? isSameDay(day, selected) : false
          const isFrom = previewFrom ? isSameDay(day, previewFrom) : false
          const isTo = previewTo ? isSameDay(day, previewTo) : false
          const inRange =
            previewFrom && previewTo
              ? isInRange(day, previewFrom, previewTo)
              : false
          const isToday = isSameDay(day, today)
          const isEndpoint = isFrom || isTo || isSelected

          return (
            <button
              key={key}
              type="button"
              className={cn(
                'relative flex h-8 items-center justify-center rounded-md text-sm transition-opacity duration-150 hover:opacity-90',
                inRange && !isEndpoint && 'bg-primary/15 text-foreground',
                isEndpoint && 'bg-primary text-primary-foreground',
                !isEndpoint && !inRange && 'text-foreground',
                isToday && !isEndpoint && 'border border-border',
              )}
              onClick={() => onSelectDay(day)}
              onMouseEnter={() => onHoverDayChange?.(day)}
              onMouseLeave={() => onHoverDayChange?.(null)}
            >
              {day.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}
